/**
 * Publication body previews (temporary excerpt fallback)
 *
 * While the AI excerpt campaign runs, most published publications have no
 * excerpt. content_html is not selectable from the client, so the opening
 * ~220 characters are computed on read by the get_publication_previews RPC
 * (nothing is stored). Used only when excerpt is null/empty/whitespace —
 * any real excerpt always wins. Remove once the campaign completes.
 */

import { db } from '@/lib/supabaseQuery';
import { logger } from '@/utils/logger';

interface PreviewRow {
  id: string;
  preview: string | null;
}

/**
 * Fetch body-derived previews for the given publication ids in one batched
 * RPC call. Returns a Map of id -> preview (only rows with non-empty text).
 */
export async function fetchPublicationPreviews(ids: string[]): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  const unique = [...new Set(ids)].filter(Boolean);
  if (unique.length === 0) return map;

  try {
    const { data, error } = await db.raw.rpc('get_publication_previews', { p_ids: unique });
    if (error) {
      logger.error('Failed to fetch publication previews', { error: error.message, count: unique.length });
      return map;
    }
    for (const row of (data ?? []) as PreviewRow[]) {
      const preview = row.preview?.trim();
      if (row.id && preview) map.set(row.id, preview);
    }
  } catch (error) {
    logger.error('Failed to fetch publication previews', { error, count: unique.length });
  }
  return map;
}

/** True when an excerpt is missing or whitespace-only. */
export function needsPreview(excerpt: string | null | undefined): boolean {
  return !excerpt || excerpt.trim().length === 0;
}
